import React from 'react';
import { Accessibility, Eye, Ear, Hand, Brain, Monitor, Smartphone, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const AccessibilityPage: React.FC = () => {
  const lastUpdated = 'December 15, 2024';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <Accessibility className="h-8 w-8 text-purple-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Accessibility Statement</h1>
          <p className="text-lg text-gray-600">
            Committed to making mental health accessible to everyone
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Accessibility className="h-6 w-6 text-purple-600 mr-3" />
                Our Commitment to Accessibility
              </h2>
              <p className="text-gray-700 mb-4">
                TherapistDirect is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards.
              </p>
              <p className="text-gray-700">
                We believe that mental health support should be accessible to everyone, regardless of their abilities or disabilities. Our platform is designed to be inclusive and usable by people with various accessibility needs.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Monitor className="h-6 w-6 text-purple-600 mr-3" />
                Accessibility Features
              </h2>
              <p className="text-gray-700 mb-4">
                Our platform includes the following accessibility features:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Eye className="h-5 w-5 text-blue-600 mr-2" />
                    Visual Accessibility
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• High contrast color schemes</li>
                    <li>• Adjustable font sizes</li>
                    <li>• Clear typography and spacing</li>
                    <li>• Alternative text for images</li>
                    <li>• Focus indicators for navigation</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Ear className="h-5 w-5 text-green-600 mr-2" />
                    Audio Accessibility
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Screen reader compatibility</li>
                    <li>• Keyboard navigation support</li>
                    <li>• Audio descriptions for videos</li>
                    <li>• Captioning for multimedia content</li>
                    <li>• Voice command support</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Hand className="h-5 w-5 text-yellow-600 mr-2" />
                    Motor Accessibility
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Keyboard-only navigation</li>
                    <li>• Large click targets</li>
                    <li>• Voice control compatibility</li>
                    <li>• Adjustable timing for interactions</li>
                    <li>• Alternative input methods</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Brain className="h-5 w-5 text-red-600 mr-2" />
                    Cognitive Accessibility
                  </h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Simple, clear language</li>
                    <li>• Consistent navigation patterns</li>
                    <li>• Error prevention and recovery</li>
                    <li>• Logical content organization</li>
                    <li>• Multiple ways to complete tasks</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Smartphone className="h-6 w-6 text-purple-600 mr-3" />
                Mobile Accessibility
              </h2>
              <p className="text-gray-700 mb-4">
                Our platform is fully responsive and accessible on mobile devices:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Touch-friendly interface with appropriate target sizes</li>
                <li>Responsive design that works on all screen sizes</li>
                <li>Mobile-optimized navigation and controls</li>
                <li>Voice input and output capabilities</li>
                <li>Gesture support for common interactions</li>
                <li>Optimized for both portrait and landscape orientations</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Compliance Standards</h2>
              <p className="text-gray-700 mb-4">
                We strive to meet or exceed the following accessibility standards:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700"><strong>WCAG 2.1 AA:</strong> Web Content Accessibility Guidelines</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700"><strong>Section 508:</strong> Rehabilitation Act accessibility requirements</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700"><strong>ADA Compliance:</strong> Americans with Disabilities Act</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-700"><strong>WCAG 2.1 AAA:</strong> Working towards highest level</span>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Assistive Technology Support</h2>
              <p className="text-gray-700 mb-4">
                Our platform is tested and optimized for compatibility with:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Screen Readers:</strong> JAWS, NVDA, VoiceOver, TalkBack</li>
                <li><strong>Voice Recognition:</strong> Dragon NaturallySpeaking, Voice Control</li>
                <li><strong>Magnification Software:</strong> ZoomText, Magnifier</li>
                <li><strong>Switch Devices:</strong> Head pointers, eye tracking, sip-and-puff</li>
                <li><strong>Alternative Keyboards:</strong> One-handed, ergonomic, on-screen</li>
                <li><strong>Mobile Accessibility:</strong> iOS VoiceOver, Android TalkBack</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Testing and Evaluation</h2>
              <p className="text-gray-700 mb-4">
                We regularly evaluate our platform's accessibility through:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Automated accessibility testing tools</li>
                <li>Manual testing by accessibility experts</li>
                <li>User testing with people who have disabilities</li>
                <li>Regular audits and compliance reviews</li>
                <li>Feedback from accessibility advocates</li>
                <li>Continuous monitoring and improvement</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Known Limitations</h2>
              <p className="text-gray-700 mb-4">
                While we strive for full accessibility, we acknowledge some current limitations:
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <HelpCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Areas for Improvement</h4>
                    <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                      <li>• Some third-party integrations may have limited accessibility</li>
                      <li>• Advanced features may require additional accessibility enhancements</li>
                      <li>• Mobile app accessibility is continuously being improved</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Feedback and Support</h2>
              <p className="text-gray-700 mb-4">
                We welcome feedback on accessibility issues and suggestions for improvement:
              </p>
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <p className="text-gray-700">
                  <strong>Accessibility Support:</strong> accessibility@therapistdirect.com
                </p>
                <p className="text-gray-700">
                  <strong>General Support:</strong> support@therapistdirect.com
                </p>
                <p className="text-gray-700">
                  <strong>Phone Support:</strong> 1-800-WELLNESS
                </p>
                <p className="text-gray-700">
                  <strong>Response Time:</strong> We aim to respond to accessibility concerns within 48 hours
                </p>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Alternative Access Methods</h2>
              <p className="text-gray-700 mb-4">
                If you experience accessibility barriers, we offer alternative ways to access our services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Phone Support:</strong> Call our accessibility hotline for assistance</li>
                <li><strong>Email Support:</strong> Send detailed requests via email</li>
                <li><strong>Video Calls:</strong> Schedule accessibility support sessions</li>
                <li><strong>Documentation:</strong> Request accessible formats of our content</li>
                <li><strong>Training:</strong> Receive personalized platform training</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Continuous Improvement</h2>
              <p className="text-gray-700 mb-4">
                We are committed to continuously improving accessibility:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Regular accessibility audits and testing</li>
                <li>User feedback integration and analysis</li>
                <li>Staff training on accessibility best practices</li>
                <li>Partnership with accessibility organizations</li>
                <li>Staying current with accessibility standards and technology</li>
                <li>Regular updates to improve accessibility features</li>
              </ul>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For accessibility-related questions, concerns, or feedback:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-gray-700">
                  <strong>Accessibility Team:</strong> accessibility@therapistdirect.com
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> 1-800-WELLNESS (ask for accessibility support)
                </p>
                <p className="text-gray-700">
                  <strong>Address:</strong> 123 Wellness St, Suite 400, Mental Health City, MH 12345
                </p>
                <p className="text-gray-700">
                  <strong>Response Time:</strong> 48 hours for accessibility concerns
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <p className="text-sm text-gray-500">
                  © {new Date().getFullYear()} TherapistDirect. All rights reserved.
                </p>
                <div className="flex space-x-6 text-sm">
                  <Link to="/privacy" className="text-purple-600 hover:text-purple-800">
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="text-purple-600 hover:text-purple-800">
                    Terms of Service
                  </Link>
                  <Link to="/" className="text-purple-600 hover:text-purple-800">
                    Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPage;
